import React, { createContext, useContext, useState, useEffect } from 'react';
import { customNetwork } from '../config/networkConfig';
import Web3 from 'web3';

interface WalletContextType {
  account: string | null;
  balance: string;
  networkId: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  isConnected: boolean;
  switchNetwork: () => Promise<void>;
  switchAccount: () => Promise<void>; // New method for switching accounts
}

export const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const WalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [account, setAccount] = useState<string | null>(null);
  const [balance, setBalance] = useState<string>('0');
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [networkId, setNetworkId] = useState<string | null>(null);

  const fetchBalance = async (address: string) => {
    console.log('Fetching balance for address:', address);
    if (window.ethereum && address) {
      try {
        const web3 = new Web3(window.ethereum);
        const weiBalance = await web3.eth.getBalance(address);
        const etherBalance = web3.utils.fromWei(weiBalance, 'ether');
        console.log('Balance in wei:', weiBalance);
        console.log('Balance in ether:', etherBalance);
        setBalance(etherBalance);
      } catch (error) {
        console.error('Error fetching balance:', error);
        setBalance('0');
      }
    }
  };

  const fetchNetworkId = async () => {
    if (window.ethereum) {
      try {
        const chainId = await window.ethereum.request({ method: 'eth_chainId' });
        setNetworkId(chainId);
      } catch (error) {
        console.error('Error fetching network ID:', error);
        setNetworkId(null);
      }
    }
  };

  const switchNetwork = async () => {
    if (window.ethereum) {
      try {
        // First try to switch to the network
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: customNetwork.chainId }],
        });
      } catch (switchError: any) {
        // This error code indicates that the chain has not been added to MetaMask
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: customNetwork.chainId,
                  chainName: customNetwork.chainName,
                  nativeCurrency: customNetwork.nativeCurrency,
                  rpcUrls: customNetwork.rpcUrls,
                  blockExplorerUrls: customNetwork.blockExplorerUrls,
                },
              ],
            });
          } catch (addError) {
            console.error('Failed to add network:', addError);
          }
        } else {
          console.error('Failed to switch network:', switchError);
        }
      }
      
      // Update the network ID after switching
      await fetchNetworkId();
    }
  };

  // New method to explicitly request permission to switch accounts
  const switchAccount = async () => {
    if (window.ethereum) {
      try {
        console.log("Requesting permission to access/switch accounts...");
        await window.ethereum.request({
          method: "wallet_requestPermissions",
          params: [{ eth_accounts: {} }],
        });
        
        // After permission is granted, fetch the currently selected account
        const accounts = await window.ethereum.request({ method: "eth_accounts" });
        
        if (accounts && accounts.length > 0) {
          console.log("Selected account:", accounts[0]);
          setAccount(accounts[0]);
          setIsConnected(true);
          await fetchBalance(accounts[0]);
        } else {
          console.error("No accounts returned after permission request");
        }
      } catch (error) {
        console.error("Error requesting account permissions:", error);
      }
    } else {
      console.error("MetaMask is not installed");
    }
  };

  const connect = async () => {
    if (window.ethereum) {
      try {
        // First, request permissions to ensure we get the latest account
        console.log("Requesting permissions to get the latest selected account...");
        await window.ethereum.request({
          method: "wallet_requestPermissions",
          params: [{ eth_accounts: {} }],
        });
        
        // Now get the accounts after the permission request
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        
        if (accounts && accounts.length > 0) {
          console.log("Connected to account:", accounts[0]);
          setAccount(accounts[0]);
          setIsConnected(true);
          await fetchBalance(accounts[0]);
          await fetchNetworkId();
          await switchNetwork();
        } else {
          // Fallback to legacy approach if permission request doesn't return accounts
          console.log("No accounts from permission request, using eth_requestAccounts...");
          const requestedAccounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
          
          if (requestedAccounts && requestedAccounts.length > 0) {
            setAccount(requestedAccounts[0]);
            setIsConnected(true);
            await fetchBalance(requestedAccounts[0]);
            await fetchNetworkId();
            await switchNetwork();
          } else {
            console.error("No accounts available after connection attempts");
          }
        }
      } catch (error) {
        console.error('Failed to connect wallet:', error);
      }
    } else {
      console.error('MetaMask is not installed');
    }
  };

  const disconnect = () => {
    setAccount(null);
    setBalance('0');
    setNetworkId(null);
    setIsConnected(false);
  };

  // Listen for account changes
  useEffect(() => {
    const handleAccountsChanged = async (accounts: string[]) => {
      console.log('ACCOUNTS CHANGED EVENT FIRED:', accounts);
      if (accounts.length > 0) {
        setAccount(accounts[0]);
        setIsConnected(true);
        await fetchBalance(accounts[0]);
      } else {
        // User disconnected all accounts
        disconnect();
      }
    };

    const handleChainChanged = async (chainId: string) => {
      console.log('CHAIN CHANGED EVENT FIRED:', chainId);
      setNetworkId(chainId);
      if (account) {
        await fetchBalance(account);
      }
    };

    if (window.ethereum) {
      // Set up listeners
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);

      // Initial check for connected accounts
      const checkConnectedAccounts = async () => {
        if (window.ethereum) {
          try {
            const accounts = await window.ethereum.request({ method: 'eth_accounts' });
            if (accounts && accounts.length > 0) {
              console.log('Found connected account:', accounts[0]);
              setAccount(accounts[0]);
              setIsConnected(true);
              await fetchBalance(accounts[0]);
              await fetchNetworkId();
            }
          } catch (error) {
            console.error('Error checking connected accounts:', error);
          }
        }
      };
      
      checkConnectedAccounts();

      // Cleanup listeners when component unmounts
      return () => {
        if (window.ethereum) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
        }
      };
    }
  }, []); // Empty dependency array means this effect runs once on mount
  
  // Handle account updates
  useEffect(() => {
    if (account) {
      fetchBalance(account);
    }
  }, [account]);

  // Handle account updates
  useEffect(() => {
    if (account) {
      fetchBalance(account);
    }
  }, [account, isConnected]);

  return (
    <WalletContext.Provider value={{ 
      account, 
      balance, 
      networkId,
      connect, 
      disconnect, 
      isConnected,
      switchNetwork,
      switchAccount // Add the new method to the context
    }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};